import type { Layout, Data } from "plotly.js";

type PlotlyFigure = {
  data: Data[];
  layout: Partial<Layout> & {
    meta?: any;
    margin?: Partial<Layout["margin"]>;
  };
};

function clampFont(size: number): number {
  return Math.max(6, Math.min(18, size)); // tweak if you want bigger min/max
}

export function scaleFigureToPanel(
  figure: PlotlyFigure,
  targetWidthPx: number,
  targetHeightPx: number
): PlotlyFigure {
  // deep clone so we never mutate the store copy
  const fig = JSON.parse(JSON.stringify(figure)) as PlotlyFigure;

  const layout = (fig.layout ||= {});
  
  // Explicitly preserve coloraxis (for heatmaps and continuous color scales)
  // JSON serialization should preserve it, but be explicit to ensure it's not lost
  if (figure.layout?.coloraxis && !layout.coloraxis) {
    layout.coloraxis = figure.layout.coloraxis;
  }
  const meta = (layout.meta as any) || {};
  const lumi = meta.lumi || {};
  const baseLayout = lumi.base_layout || {};

  const baseWidth =
    typeof baseLayout.width === "number"
      ? baseLayout.width
      : (layout.width as number) || 700;
  const baseHeight =
    typeof baseLayout.height === "number"
      ? baseLayout.height
      : (layout.height as number) || 500;
  const baseFontSize =
    typeof baseLayout.font_size === "number"
      ? baseLayout.font_size
      : (layout.font && (layout.font as any).size) || 14;

  // keep aspect ratio – scale factor is min of x/y
  const sx = targetWidthPx / baseWidth;
  const sy = targetHeightPx / baseHeight;
  const scale = Math.min(sx, sy);

  // scaled layout width/height
  layout.width = Math.round(baseWidth * scale);
  layout.height = Math.round(baseHeight * scale);

  // global font
  layout.font = layout.font || {};
  (layout.font as any).size = clampFont(baseFontSize * scale);

  // title + subtitle fonts
  if (layout.title) {
    const title: any = layout.title;
    if (title.font && typeof title.font.size === "number") {
      title.font.size = clampFont(title.font.size * scale);
    }
    if (title.subtitle && title.subtitle.font && typeof title.subtitle.font.size === "number") {
      title.subtitle.font.size = clampFont(title.subtitle.font.size * scale);
    }
  }

  // margins
  const margin = (layout.margin = layout.margin || {});
  margin.l = Math.round((margin.l ?? 64) * scale);
  margin.r = Math.round((margin.r ?? 80) * scale);
  margin.t = Math.round((margin.t ?? 120) * scale);
  margin.b = Math.round((margin.b ?? 56) * scale);

  const scaleAxis = (axis: any) => {
    if (!axis) return;
    if (axis.title && axis.title.font && typeof axis.title.font.size === "number") {
      axis.title.font.size = clampFont(axis.title.font.size * scale);
    }
    if (axis.tickfont && typeof axis.tickfont.size === "number") {
      axis.tickfont.size = clampFont(axis.tickfont.size * scale);
    }
  };

  // scale the main few axes (safe: only scale if they exist)
  const axisKeys = [
    "xaxis",
    "yaxis",
    "xaxis2",
    "yaxis2",
    "xaxis3",
    "yaxis3",
    "xaxis4",
    "yaxis4",
  ];
  axisKeys.forEach((k) => {
    if ((layout as any)[k]) scaleAxis((layout as any)[k]);
  });

  // scale annotations (facet titles, text annotations, etc.)
  if (Array.isArray(layout.annotations)) {
    layout.annotations.forEach((ann: any) => {
      if (ann && ann.font && typeof ann.font.size === "number") {
        ann.font.size = clampFont(ann.font.size * scale);
      }
    });
  }

  // scale legend properties comprehensively
  if (layout.legend) {
    const legend = layout.legend as any;
    
    // Scale legend font size
    if (legend.font && typeof legend.font.size === "number") {
      legend.font.size = clampFont(legend.font.size * scale);
    }
    
    // Scale legend title font size
    if (legend.title && legend.title.font && typeof legend.title.font.size === "number") {
      legend.title.font.size = clampFont(legend.title.font.size * scale);
    }
    
    // Scale legend border width
    if (typeof legend.borderwidth === "number") {
      legend.borderwidth = legend.borderwidth * scale;
    }
    
    // Scale legend item width/height (affects marker size in legend)
    if (typeof legend.itemwidth === "number") {
      legend.itemwidth = legend.itemwidth * scale;
    }
    if (typeof legend.itemheight === "number") {
      legend.itemheight = legend.itemheight * scale;
    }
    
    // Scale legend spacing (gap between items/groups)
    if (typeof legend.tracegroupgap === "number") {
      legend.tracegroupgap = legend.tracegroupgap * scale;
    }
  }

  // traces: markers, lines, colorbars
  fig.data?.forEach((trace: any) => {
    if (trace.marker) {
      const m = trace.marker;

      if (typeof m.size === "number") {
        m.size = m.size * scale;
      } else if (Array.isArray(m.size)) {
        m.size = m.size.map((v: any) => (typeof v === "number" ? v * scale : v));
      }

      if (m.line && typeof m.line.width === "number") {
        m.line.width = m.line.width * scale;
      }

      const cb = m.colorbar;
      if (cb) {
        if (typeof cb.thickness === "number") {
          cb.thickness = cb.thickness * scale;
        }
        if (cb.tickfont && typeof cb.tickfont.size === "number") {
          cb.tickfont.size = clampFont(cb.tickfont.size * scale);
        }
      }
    }

    if (trace.line && typeof trace.line.width === "number") {
      trace.line.width = trace.line.width * scale;
    }

    const colorbar = trace.colorbar;
    if (colorbar) {
      if (typeof colorbar.thickness === "number") {
        colorbar.thickness = colorbar.thickness * scale;
      }
      if (colorbar.tickfont && typeof colorbar.tickfont.size === "number") {
        colorbar.tickfont.size = clampFont(colorbar.tickfont.size * scale);
      }
    }
  });

  // Ensure coloraxis is preserved in final return
  if (figure.layout?.coloraxis && !layout.coloraxis) {
    layout.coloraxis = figure.layout.coloraxis;
  }

  return fig;
}

